import { Controller } from '@hotwired/stimulus';
import { Editor } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';

/**
 * TipTapEditor制御 入力側
 */
export default class extends Controller<HTMLDivElement> {
  static override targets: string[] = [
    'mount',
    'input',
    'bold',
    'italic',
    'underline',
    'strike',
    'h1',
    'h2',
    'h3',
    'blockquote',
    'bullet',
    'ordered',
    'colorPalette',
    'colorToggle',
    'colorIndicator',
    'colorPicker',
    'alignLeft',
    'alignCenter',
    'alignRight',
    'alignJustify',
    'link',
  ];

  static override values = {
    initial: String,
  };

  declare readonly mountTarget: HTMLDivElement;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly boldTarget: HTMLButtonElement;
  declare readonly italicTarget: HTMLButtonElement;
  declare readonly underlineTarget: HTMLButtonElement;
  declare readonly strikeTarget: HTMLButtonElement;
  declare readonly h1Target: HTMLButtonElement;
  declare readonly h2Target: HTMLButtonElement;
  declare readonly h3Target: HTMLButtonElement;
  declare readonly blockquoteTarget: HTMLButtonElement;
  declare readonly bulletTarget: HTMLButtonElement;
  declare readonly orderedTarget: HTMLButtonElement;
  declare readonly colorPaletteTarget: HTMLDivElement;
  declare readonly colorToggleTarget: HTMLButtonElement;
  declare readonly colorIndicatorTarget: HTMLSpanElement;
  declare readonly colorPickerTarget: HTMLInputElement;
  declare readonly hasColorPaletteTarget: boolean;
  declare readonly hasColorIndicatorTarget: boolean;
  declare readonly hasColorPickerTarget: boolean;
  declare readonly alignLeftTarget: HTMLButtonElement;
  declare readonly alignCenterTarget: HTMLButtonElement;
  declare readonly alignRightTarget: HTMLButtonElement;
  declare readonly alignJustifyTarget: HTMLButtonElement;
  declare readonly linkTarget: HTMLButtonElement;

  // HTMLの方にdata-rich-text-initial-value="..."を指定することで取得できる
  declare readonly initialValue?: string;

  private editor: Editor | undefined;
  private boundCloseColorPalette: ((event: Event) => void) | undefined;

  override connect(): void {
    this.initializeRichText();
    this.boundCloseColorPalette = this.closeColorPaletteOnOutsideClick.bind(this);
    document.addEventListener('click', this.boundCloseColorPalette);
  }

  override disconnect(): void {
    this.editor?.destroy();
    this.editor = undefined;
    if (this.boundCloseColorPalette) {
      document.removeEventListener('click', this.boundCloseColorPalette);
    }
  }

  /**
   * TipTap初期化
   */
  private initializeRichText() {
    const initialContent = this.inputTarget?.value || this.initialValue || '';

    // 編集部分の描画はControllerから行われるため、スタイルはここで定義する必要がある
    const editorClass = 'min-h-60 focus:outline-none rich-text';

    /**
     * Editorの初期値について
     * @link https://tiptap.dev/docs/editor/api/editor
     */
    this.editor = new Editor({
      element: this.mountTarget,
      extensions: [
        StarterKit,
        TextStyle,
        Color,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
      ],
      injectCSS: true,
      editorProps: {
        attributes: {
          class: editorClass,
        },
      },
      content: initialContent,
      onUpdate: ({ editor }) => {
        this.inputTarget.value = editor.getHTML();
        this.updateToolbarActiveState(editor);
      },
      onSelectionUpdate: ({ editor }) => {
        this.updateToolbarActiveState(editor);
      },
    });

    this.updateToolbarActiveState(this.editor);
  }

  // Formatting actions
  toggleBold(): void {
    this.editor?.chain().focus().toggleBold().run();
  }

  toggleItalic(): void {
    this.editor?.chain().focus().toggleItalic().run();
  }

  toggleStrike(): void {
    this.editor?.chain().focus().toggleStrike().run();
  }

  toggleUnderline(): void {
    this.editor?.chain().focus().toggleUnderline().run();
  }

  // Headings
  toggleHeading1(): void {
    this.editor?.chain().focus().toggleHeading({ level: 1 }).run();
  }

  toggleHeading2(): void {
    this.editor?.chain().focus().toggleHeading({ level: 2 }).run();
  }

  toggleHeading3(): void {
    this.editor?.chain().focus().toggleHeading({ level: 3 }).run();
  }

  // Blockquote & Lists
  toggleBlockquote(): void {
    this.editor?.chain().focus().toggleBlockquote().run();
  }

  toggleBulletList(): void {
    this.editor?.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList(): void {
    this.editor?.chain().focus().toggleOrderedList().run();
  }

  // Text Align
  setAlignLeft(): void {
    this.editor?.chain().focus().setTextAlign('left').run();
  }

  setAlignCenter(): void {
    this.editor?.chain().focus().setTextAlign('center').run();
  }

  setAlignRight(): void {
    this.editor?.chain().focus().setTextAlign('right').run();
  }

  setAlignJustify(): void {
    this.editor?.chain().focus().setTextAlign('justify').run();
  }

  // Link
  setLink(): void {
    const previousUrl = this.editor?.getAttributes('link').href;
    const url = window.prompt('URLを入力してください', previousUrl || '');

    // キャンセルされた場合
    if (url === null) {
      return;
    }

    // URLが空の場合はリンクを削除
    if (url === '') {
      this.editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // URLを設定
    this.editor
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' })
      .run();
  }

  unsetLink(): void {
    this.editor?.chain().focus().unsetLink().run();
  }

  private updateToolbarActiveState(editor?: Editor): void {
    const ed = editor ?? this.editor;
    if (!ed) return;

    const targets = [
      { target: this.boldTarget, name: 'bold', attributes: null },
      { target: this.italicTarget, name: 'italic', attributes: null },
      { target: this.underlineTarget, name: 'underline', attributes: null },
      { target: this.strikeTarget, name: 'strike', attributes: null },
      { target: this.h1Target, name: 'heading', attributes: { level: 1 } },
      { target: this.h2Target, name: 'heading', attributes: { level: 2 } },
      { target: this.h3Target, name: 'heading', attributes: { level: 3 } },
      { target: this.blockquoteTarget, name: 'blockquote', attributes: null },
      { target: this.bulletTarget, name: 'bulletList', attributes: null },
      { target: this.orderedTarget, name: 'orderedList', attributes: null },
      { target: this.alignLeftTarget, name: null, attributes: { textAlign: 'left' } },
      { target: this.alignCenterTarget, name: null, attributes: { textAlign: 'center' } },
      { target: this.alignRightTarget, name: null, attributes: { textAlign: 'right' } },
      { target: this.alignJustifyTarget, name: null, attributes: { textAlign: 'justify' } },
      { target: this.linkTarget, name: 'link', attributes: null },
    ];

    targets.forEach(({ target, name, attributes }) => {
      if (target) {
        const isActive = name
          ? attributes
            ? ed.isActive(name, attributes) // nameがあり、attributesがある場合
            : ed.isActive(name) // nameがあり、attributesがない場合
          : attributes
            ? ed.isActive(attributes) // nameがなく、attributesがある場合
            : false; // nameがなく、attributesがない場合
        this.setActive(target, isActive);
      }
    });

    this.setColor(ed);
  }

  private setActive(button: HTMLButtonElement | undefined, isActive: boolean): void {
    if (!button) return;

    button.setAttribute('aria-pressed', String(isActive));
  }

  /**
   * カラーインジケーターの色を即時更新
   */
  private updateColorIndicator(color: string): void {
    if (this.hasColorIndicatorTarget) {
      this.colorIndicatorTarget.style.backgroundColor = color;
    }
    if (this.hasColorPickerTarget) {
      this.colorPickerTarget.value = color;
    }
  }

  /**
   * カラーインジケーターの色を現在のテキスト色に同期
   */
  private setColor(ed: Editor): void {
    const attr = ed.getAttributes('textStyle') as { color?: string };
    const currentColor = attr?.color ? this.rgbToHex(attr.color) : '#000000';

    if (this.hasColorIndicatorTarget) {
      this.colorIndicatorTarget.style.backgroundColor = currentColor;
    }
    if (this.hasColorPickerTarget) {
      this.colorPickerTarget.value = currentColor;
    }
  }

  /**
   * カラーパレットの表示/非表示を切り替える
   */
  toggleColorPalette(event: Event): void {
    event.stopPropagation();
    if (!this.hasColorPaletteTarget) return;

    const isHidden = this.colorPaletteTarget.classList.contains('hidden');
    if (isHidden) {
      this.colorPaletteTarget.classList.remove('hidden');
      this.colorToggleTarget.setAttribute('aria-expanded', 'true');
      this.highlightActiveColor();
    } else {
      this.colorPaletteTarget.classList.add('hidden');
      this.colorToggleTarget.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * カラーパレットから色を適用する
   */
  applyColor(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const color = button.dataset.color;
    if (!color) return;

    this.editor?.chain().focus().setColor(color).run();
    this.updateColorIndicator(color);
    this.closeColorPalette();
  }

  /**
   * カラーピッカー（自由選択）から色を適用する
   */
  changeColor(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input?.value;
    if (!value) return;
    this.editor?.chain().focus().setColor(value).run();
    this.updateColorIndicator(value);
  }

  /**
   * テキストの色をリセット（デフォルトに戻す）
   */
  removeColor(): void {
    this.editor?.chain().focus().unsetColor().run();
    this.updateColorIndicator('#000000');
    this.closeColorPalette();
  }

  /**
   * カラーパレットを閉じる
   */
  private closeColorPalette(): void {
    if (!this.hasColorPaletteTarget) return;
    this.colorPaletteTarget.classList.add('hidden');
    this.colorToggleTarget.setAttribute('aria-expanded', 'false');
  }

  /**
   * パレット外クリックで閉じる
   */
  private closeColorPaletteOnOutsideClick(event: Event): void {
    if (!this.hasColorPaletteTarget) return;
    const target = event.target as Node;

    if (
      this.colorPaletteTarget.contains(target) ||
      this.colorToggleTarget.contains(target)
    ) {
      return;
    }

    this.closeColorPalette();
  }

  /**
   * 現在適用中の色にハイライトを表示
   */
  private highlightActiveColor(): void {
    if (!this.hasColorPaletteTarget || !this.editor) return;

    const attr = this.editor.getAttributes('textStyle') as { color?: string };
    const currentColor = attr?.color ? this.rgbToHex(attr.color) : '#000000';
    const normalizedCurrent = currentColor.toLowerCase();

    const buttons = this.colorPaletteTarget.querySelectorAll<HTMLButtonElement>('[data-color]');
    buttons.forEach((btn) => {
      const btnColor = btn.dataset.color?.toLowerCase();
      if (btnColor === normalizedCurrent) {
        btn.classList.add('ring-2', 'ring-offset-1', 'ring-gray-800');
      } else {
        btn.classList.remove('ring-2', 'ring-offset-1', 'ring-gray-800');
      }
    });
  }

  /**
   * rgb(255, 255, 255, 1) から #ffffff に変換
   * rgb(255, 255, 255)の形式ではない場合はそのまま返す
   *
   * @param input string like rgba(255, 255, 255, 1)
   * @returns string like #ffffff
   */
  private rgbToHex(input: string): string {
    const rgbPattern = /^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
    const match = input.trim().match(rgbPattern);

    // rgb(255, 255, 255)の形式ではない場合はそのまま返す
    if (!match) return input;

    const rStr = match[1];
    const gStr = match[2];
    const bStr = match[3];

    // グループが取得できない場合はそのまま返す
    if (!rStr || !gStr || !bStr) return input;

    // 10進数で数字に変換
    const r = parseInt(rStr, 10);
    const g = parseInt(gStr, 10);
    const b = parseInt(bStr, 10);

    // 16進数に変換（2桁にパディング）
    const hexR = r.toString(16).padStart(2, '0');
    const hexG = g.toString(16).padStart(2, '0');
    const hexB = b.toString(16).padStart(2, '0');

    return `#${hexR}${hexG}${hexB}`;
  }
}
